import {useCallback, useState} from 'react';
import {Edge, Node} from 'reactflow';
import Button from '@mui/material/Button';
import {useMutation, useQueryClient} from 'react-query';
import {useSnackbar} from 'notistack';
import {useTranslation} from 'react-i18next';
import {AutomationStatus, NodeType} from 'crqlar-react-common/dist/models/automation/automation';
import {AutomationService} from 'crqlar-react-common/dist/services/automation/AutomationService';
import {getErrorMessage} from 'crqlar-react-common/dist/utils/http.util';

import {FLOW_ACTIVITY_QUERY_KEY} from './useFlowActivity';
import {remindNowMessage, remindNowOutcome} from '../utils/remindNow';

interface UseEditorManualRunOptions {
    flowId?: string;
    flowStatus?: AutomationStatus;
    readOnly: boolean;
    nodes: Node[];
    edges: Edge[];
    isDirty: boolean;
    /** False when the canvas was edited while it saved. */
    saveCanvas: () => Promise<boolean>;
    onViewExecution: (executionId: string) => void;
}

/** A manual trigger card's "Run for everyone": a live run after confirmation; unsaved changes are saved first. */
export const useEditorManualRun = ({
    flowId,
    flowStatus,
    readOnly,
    nodes,
    edges,
    isDirty,
    saveCanvas,
    onViewExecution
}: UseEditorManualRunOptions) => {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const queryClient = useQueryClient();
    const [manualRunConfirmNodeId, setManualRunConfirmNodeId] = useState<string | null>(null);

    const getManualRunDisabledReason = useCallback((nodeId: string) => readOnly
        ? t('You do not have permission to run this automation.')
        : flowStatus !== AutomationStatus.ACTIVE
            ? t('Set the automation live before running it for everyone. You can still test it from the editor header.')
            : !edges.some(edge => edge.source === nodeId)
                ? t('Connect a step to this trigger before running it.')
                : undefined, [readOnly, flowStatus, edges, t]);

    const {mutate: runManualTrigger, isLoading: manualRunLoading, variables: manualRunNodeId} = useMutation({
        mutationFn: async (nodeId: string) => {
            const manualRunDisabledReason = getManualRunDisabledReason(nodeId);
            if (manualRunDisabledReason) throw new Error(manualRunDisabledReason);
            if (!flowId || !nodes.some(node => node.id === nodeId && node.data.type === NodeType.MANUAL_TRIGGER)) {
                throw new Error(t('The manual trigger is no longer available.'));
            }

            if (isDirty && !(await saveCanvas())) {
                throw new Error(t('The automation changed while saving. Run it again to include the latest changes.'));
            }

            return AutomationService.runManualTrigger(flowId, nodeId);
        },
        onSuccess: execution => {
            setManualRunConfirmNodeId(null);
            void queryClient.invalidateQueries([FLOW_ACTIVITY_QUERY_KEY, flowId]);
            // "Remind now" says how many reminders it queued; with none there is no run to view.
            const remindNow = remindNowOutcome(execution);
            enqueueSnackbar(remindNow ? remindNowMessage(remindNow, t) : t('Run queued'), {
                variant: remindNow?.reminders === 0 ? 'info' : 'success',
                action: execution.id ? (
                    <Button color={'inherit'} size={'small'} onClick={() => onViewExecution(execution.id!)}>
                        {t('View run')}
                    </Button>
                ) : undefined
            });
        },
        onError: error => {
            enqueueSnackbar(getErrorMessage(error, t('The run could not be started')), {variant: 'error'});
        }
    });

    return {
        manualRunConfirmNodeId,
        setManualRunConfirmNodeId,
        getManualRunDisabledReason,
        runManualTrigger,
        manualRunLoading,
        manualRunNodeId
    };
};
